import {PolymerElement} from '@polymer/polymer/polymer-element.js';
import {html} from '@polymer/polymer/lib/utils/html-tag.js';
import {CREDIT_CARD_TRANSLATIONS} from './credit-card-translations.js';
import {validations} from './credit-card-format.js';
import template from './ns-credit-card.html';
import {FormMixin} from '../formMixin.js';
import {LuhnCheck} from '@nuskin/ns-shop';

/**
 * This is the Credit Card Component. Displays an input for credit cards.
 * Validates with regex and aem luhn checker for on-blur event of the input box.
 * Checks for required input.
 *
 * <section>
 *  <h2>Content Guidelines</h2>
 *  <p>Here are some guidelines on how to properly use this element.</p>
 * </section>
 *
 * <h2>Credit Card Component Demo</h2>
 * <demo-snippet class="demoSnippet">
 *   <template>
 *     <ns-credit-card></ns-credit-card>
 *   </template>
 * </demo-snippet>
 */
export class NsCreditCard extends FormMixin(PolymerElement) {
    static get is() {
        return 'ns-credit-card';
    }

    /**
     * Sets the html template
     */
    static get template() {
        let htmlElement = document.createElement('TEMPLATE');
        htmlElement.innerHTML = template;
        return htmlElement;
    }

    /**
     * Sets properties to pass to this component
     */
    static get properties() {
        return {
            /**
             * URL for loading translations and rules data
             */
            loadDataUrl: {
                type: String
            },
            /**
             * URL for loading validation rules
             */
            loadValidationUrl: {
                type: String
            },
            /**
             * Label for the credit card input box
             */
            creditCardLabel: {
                type: String
            },
            /**
             * Credit Card typeof
             *
             * Examples:
             *   - 'VISA', 'MC', 'AMEX'
             */
            type: {
                type: String
            },
            /**
             * Credit Card number string
             */
            cardnumber: {
                type: String,
                notify: true,
                value: ''
            },
            /**
             * Error message for invalid credit card value
             */
            invalidErrorMessage: {
                type: String
            },
            /**
             * Error message for credit card number that has non-digit characters
             */
            nonDigitErrorMessage: {
                type: String
            },
            /**
             * Error message for empty credit card value
             */
            creditCardRequiredErrorMessage: {
                type: String
            },
            /**
             * Error message that will show up on UI
             */
            errorMessage: {
                type: String
            },
            /**
             * Regex expression for credit card validation based on card type
             */
            regexValidation: {
                type: String
            },
            /**
             * Country code
             *
             * Example
             *  - 'US'
             */
            country: {
                type: String
            },
            /**
             * Language code
             *
             * Example
             *  - 'en'
             */
            language: {
                type: String
            },
            /**
             * Flag that whether the credit card number is valid
             */
            valid: {
                type: Boolean,
                observer: 'validate'
            },
            /**
             * Disables all validation - this.valid returns true when disableValidation is set to true.
             */
            validationDisabled: {
                type: Boolean,
                value: false
            },
            /**
             * Disables the card number input so the user can't edit it.
             */
            inputDisabled: {
                type: Boolean,
                value: false
            },
            /**
             * Array of allowed payment types
             */
            paymentTypesAllowed: {
                type: Array
            },
            /**
             * Object containing styling information for a credit card logo
             */
            ccImageObject: {
                type: Object
            },
            /**
             * Id of the selected payment type
             */
            paymentTypeId: {
                type: Number,
                notify: true,
                observer: '_determineCardType'
            },
            /**
             * Dirty flag for ns-credit-card
             */
            dirty: {
                type: Boolean,
                value: false,
                notify: true,
                reflectToAttribute: true
            }
        };
    }

    static get observers() {
        return [
            '_updateDirtyFlag(cardNumberDirty)'
        ];
    }

    /**
     * This is a polymer lifecycle event
     * after ns-input loads to page it will run this function
     */
    ready() {
        super.ready();
        this._initialize();
        this.ccImageObject = {
            repeat: 'no-repeat',
            position: 'right 10px center'
        };
        this._determineCardType(this.paymentTypeId);
    }

    _initialize() {
        if (this.country && this.country.length === 2 && this.language && this.language.length === 2) {
            this._localize(this.language, this.country, CREDIT_CARD_TRANSLATIONS, this.serverTranslations, true);
            this.readValidations();
        }
    }

    /**
     * Reads regex string for validation from local file
     */
    readValidations() {
        //Keeping this code commented out for possible re-implementation of auto-detect credit card by number
        // if (this.paymentTypeId < 0 || this.paymentTypeId === undefined || this.paymentTypeId === null) {
        //     this.paymentTypeId = this._determineTypeByCcNumber(this.cardnumber);
        // }
        this._determineCardType(this.paymentTypeId);
        if (this.type) {
            const validation = validations[this.type.toUpperCase()];
            if (validation) {
                this.regexValidation = validation['regex'];
                this.set("creditCardMaxLength", validation['maxLength']);
            }
        }
    }

    /**
     * Validates the credit card number using the regex and luhn algorithm based
     * on the card type.
     */
    _validateCardNumber() {
        if (this.validationDisabled) {
            return;
        }
        if (!this.cardnumber) {
            this.errorMessage = this.creditCardRequiredErrorMessage;
            this.valid = false;
        } else if (!this.cardnumber.match('^[0-9]+$')) {
            this.errorMessage = this.nonDigitErrorMessage;
            this.valid = false;
        } else {
            this.readValidations();
            if (!this.type) {
                this.errorMessage = 'This payment form is not allowed in this market.';
                this.valid = false;
            } else if (this.regexValidation) {
                var regexCheckResult = this.cardnumber.match(this.regexValidation) !== null;
                /**
                 * All credit cards use Luhn algorithm for validation.
                 * https://en.wikipedia.org/wiki/Payment_card_number
                 */
                var luhnCheckResult = LuhnCheck.luhnCheck(this.cardnumber, this.paymentTypeId);
                this.valid = regexCheckResult && luhnCheckResult;
                this.errorMessage = (regexCheckResult && luhnCheckResult) ? '' : this.invalidErrorMessage;
            }
        }

        var errorElement = this.shadowRoot.querySelector('#creditCard').shadowRoot.querySelector('error-tag');
        errorElement.style.visibility = this.valid ? 'hidden' : 'visible';
        errorElement.style.display = this.valid ? 'none' : 'block';
        if (!this.valid) {
            errorElement.innerText = this.errorMessage;
        }
    }

    /**
     * Validates the value in the input against the specified regex, and also performs a Luhn check.
     * Skips validation if this.validationDisabled is set to true.
     */
    validate() {
        if (this.validationDisabled) {
            return true;
        }
        return this.valid && this.shadowRoot.querySelector('ns-form').validate();
    }

    /**
     * Determines the payment type based on a given ccNumber
     * @param {*} ccNumber
     */
    //Keeping this code commented out for possible re-implementation of auto-detect credit card by number
    // _determineTypeByCcNumber(ccNumber) {
    //     if (ccNumber && (this.paymentTypeId < 0 || this.paymentTypeId === undefined || this.paymentTypeId === null)) {
    //         if (ccNumber.match(validations.VISA.regex)) {
    //             return 0; // VISA
    //         } else if (ccNumber.match(validations.MC.regex)) {
    //             return 1; // MC
    //         } else if (ccNumber.match(validations.DISC.regex)) {
    //             return 2; // DISC
    //         } else if (ccNumber.match(validations.AMEX.regex)) {
    //             return 3; // AMEX
    //         } else if (ccNumber.match(validations.DINERS.regex)) {
    //             return 9; // Diners
    //         } else if (ccNumber.match(validations.JCB.regex)) {
    //             return 21; // JCB
    //         }
    //     }
    // }

    /**
     * Get image url based on card type
     */
    _determineCardType(paymentTypeId) {
        this.type = null;
        if (this.paymentTypesAllowed) {
            this.paymentTypesAllowed.filter(type => {
                if (type.id === paymentTypeId) {
                    this.type = type.ofsId;
                }
            });
        }

        if (this.type) {
            this.set('ccImageObject.url', nuskin.configuration.siteUrl + '/content/dam/apps/checkout/creditcards/CC-' + paymentTypeId + '.png');
        } else {
            this.set('ccImageObject.url', '');
        }
        this.set('ccImageObject.style', 'background-size: contain;')
        
        // For SEPA payment type, use IBAN label instead of default credit card label.
        this.computedCreditCardLabel = (this.type === 'SDD') ? this.ibanLabel : this.creditCardLabel;
    }
}

customElements.define(NsCreditCard.is, NsCreditCard);
